import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, TrendingUp, DollarSign, Building2, AlertCircle } from 'lucide-react';
import { safeJson } from '../../utils/api';

const EconomicCalendar = () => {
  const [calendarData, setCalendarData] = useState([]);
  const { tt } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filter, setFilter] = useState('all'); // all, earnings, dividends, splits, economic
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState('AAPL');

  const TWELVE_DATA_API_KEY = process.env.REACT_APP_TWELVE_DATA_API_KEY || 'demo';

  // Функция для определения биржи по активу
  const getExchangeForAsset = (asset) => {
    if (asset.includes('USD') && asset.length === 6) return 'FX'; // Форекс пары
    if (['BTCUSD', 'ETHUSD', 'ADAUSD', 'DOTUSD', 'LTCUSD', 'LINKUSD'].includes(asset)) return 'CC'; // Криптовалюты
    return 'NASDAQ'; // Акции по умолчанию
  };

  // Получение реальных данных экономического календаря от Investing.com
  const fetchEconomicData = async () => {
    try {
      setLoading(true);
      console.log('Fetching real economic calendar data from Investing.com...');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/economic-calendar`);
      
      if (response.ok) {
        const realData = await safeJson(response);
        console.log(`Loaded ${realData.length} real economic events`);
        
        // Сортируем данные правильно
        const sortedData = realData.sort((a, b) => {
          const today = new Date().toISOString().split('T')[0];
          const dateTimeA = new Date(`${a.date}T${a.time}`);
          const dateTimeB = new Date(`${b.date}T${b.time}`);
          
          // Если одно событие сегодня, а другое нет - сегодняшнее идет первым
          if (a.date === today && b.date !== today) return -1;
          if (b.date === today && a.date !== today) return 1;
          
          // Если оба события сегодня - сортируем по времени (раньше сверху)
          if (a.date === today && b.date === today) {
            return dateTimeA.getTime() - dateTimeB.getTime();
          }
          
          // Для будущих событий - хронологический порядок
          return dateTimeA.getTime() - dateTimeB.getTime();
        });

        setCalendarData(sortedData);
        setLastUpdated(new Date());
      } else {
        console.warn('Failed to fetch real data, falling back to test data');
        generateTestDataForAllAssets();
      }
      
    } catch (error) {
      console.error('Error fetching real economic data:', error);
      console.log('Falling back to test data...');
      generateTestDataForAllAssets();
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    console.log('Manual refresh triggered');
    await fetchEconomicData();
  };

  // Функция получения символа для разных активов
  const getSymbolForAsset = (asset) => {
    const symbolMapping = {
      'EURUSD': 'EUR',
      'GBPUSD': 'GBP',
      'USDJPY': 'USD',
      'BTCUSD': 'BTC',
      'ETHUSD': 'ETH',
      'AAPL': 'AAPL',
      'GOOGL': 'GOOGL',
      'TSLA': 'TSLA',
      'MSFT': 'MSFT',
      'AMZN': 'AMZN',
      'NFLX': 'NFLX',
      'NVDA': 'NVDA',
      'META': 'META'
    };
    return symbolMapping[asset] || asset;
  };

  // Получение тестовых данных с экономическими событиями по валютам
  const getFallbackEconomicData = (symbol) => {
    const today = new Date();
    const events = [];
    
    // Создаем события на 14 дней (7 назад + сегодня + 6 вперед)
    for (let dayOffset = -7; dayOffset <= 6; dayOffset++) {
      const eventDate = new Date(today);
      eventDate.setDate(today.getDate() + dayOffset);
      const dateStr = eventDate.toISOString().split('T')[0];
      
      // Добавляем разные события в зависимости от дня
      const dailyEvents = [];
      
      // События для USD
      if (dayOffset === 0) {
        dailyEvents.push({
          id: `usd-nfp-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '15:30',
          title: 'US Non-Farm Payrolls',
          description: 'Monthly employment report',
          impact: 'high',
          forecast: '200K',
          actual: '215K',
          previous: '190K',
          currency: 'USD'
        });
      } else if (dayOffset === 1) {
        dailyEvents.push({
          id: `usd-cpi-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '15:30',
          title: 'US CPI (Inflation)',
          description: 'Consumer Price Index',
          impact: 'high',
          forecast: '3.2%',
          actual: null,
          previous: '3.1%',
          currency: 'USD'
        });
      } else if (dayOffset === -1) {
        dailyEvents.push({
          id: `usd-fed-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '21:00',
          title: 'Fed Interest Rate Decision',
          description: 'Federal Reserve monetary policy',
          impact: 'high',
          forecast: '5.50%',
          actual: '5.50%',
          previous: '5.25%',
          currency: 'USD'
        });
      }
      
      // События для EUR
      if (dayOffset % 2 === 0) {
        if (dayOffset === 0) {
          dailyEvents.push({
            id: `eur-ecb-${dayOffset}`,
            type: 'economic',
            date: dateStr,
            time: '14:45',
            title: 'ECB Interest Rate Decision',
            description: 'European Central Bank policy',
            impact: 'high',
            forecast: '4.50%',
            actual: '4.50%',
            previous: '4.25%',
            currency: 'EUR'
          });
        } else if (dayOffset === 2) {
          dailyEvents.push({
            id: `eur-gdp-${dayOffset}`,
            type: 'economic',
            date: dateStr,
            time: '11:00',
            title: 'Eurozone GDP Growth',
            description: 'Quarterly GDP report',
            impact: 'high',
            forecast: '0.3%',
            actual: null,
            previous: '0.2%',
            currency: 'EUR'
          });
        }
      }
      
      // События для GBP
      if (dayOffset % 3 === 0) {
        dailyEvents.push({
          id: `gbp-boe-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '13:00',
          title: 'BoE Interest Rate Decision',
          description: 'Bank of England policy meeting',
          impact: 'high',
          forecast: '5.25%',
          actual: dayOffset <= 0 ? '5.25%' : null,
          previous: '5.00%',
          currency: 'GBP'
        });
      }
      
      // События для JPY
      if (dayOffset === 3) {
        dailyEvents.push({
          id: `jpy-boj-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '04:00',
          title: 'BoJ Interest Rate Decision',
          description: 'Bank of Japan policy meeting',
          impact: 'high',
          forecast: '-0.10%',
          actual: null,
          previous: '-0.10%',
          currency: 'JPY'
        });
      }
      
      events.push(...dailyEvents);
    }
    
    const economicEvents = [
    ];

    // Возвращаем все события, отсортированные по времени
    const allEvents = [...events, 
      // Дополнительные глобальные экономические события
      {
        id: 'global-oil',
        type: 'economic',
        date: new Date(today.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        time: '17:30',
        title: 'US Crude Oil Inventories',
        description: 'Weekly petroleum status report',
        impact: 'medium',
        forecast: '-2.1M',
        actual: null,
        previous: '-1.8M',
        currency: 'USD'
      },
      {
        id: 'global-gold',
        type: 'economic',
        date: new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        time: '10:00',
        title: 'Gold Price Analysis',
        description: 'Precious metals market update',
        impact: 'medium',
        forecast: '$2050',
        actual: '$2048',
        previous: '$2045',
        currency: 'USD'
      }
    ];

    // Возвращаем события в зависимости от символа
    if (symbol.includes('USD')) return allEvents.filter(e => e.currency === 'USD' || e.currency === symbol.replace('USD', ''));
    if (symbol.includes('EUR')) return allEvents.filter(e => e.currency === 'EUR' || e.currency === 'USD');
    if (symbol.includes('GBP')) return allEvents.filter(e => e.currency === 'GBP' || e.currency === 'USD');
    if (symbol.includes('JPY')) return allEvents.filter(e => e.currency === 'JPY' || e.currency === 'USD');
    
    // Для акций возвращаем все события + earnings данные для конкретной акции
    const stockEvents = [
      {
        id: `${symbol}-earnings`,
        type: 'earnings',
        date: new Date(today.getTime() + Math.floor(Math.random() * 7) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        time: '16:00',
        title: `${symbol} Earnings Report`,
        description: 'Quarterly earnings announcement',
        impact: 'high',
        forecast: `$${(2 + Math.random()).toFixed(2)}`,
        actual: Math.random() > 0.5 ? `$${(2 + Math.random()).toFixed(2)}` : null,
        previous: `$${(1.8 + Math.random()).toFixed(2)}`,
        currency: 'USD'
      },
      {
        id: `${symbol}-dividend`,
        type: 'dividends',
        date: new Date(today.getTime() - Math.floor(Math.random() * 5) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        time: '09:30',
        title: `${symbol} Dividend Payment`,
        description: 'Quarterly dividend payment',
        impact: 'medium',
        amount: (0.5 + Math.random()).toFixed(2),
        currency: 'USD'
      }
    ];
    
    return [...allEvents, ...stockEvents].sort((a, b) => {
      const dateTimeA = new Date(`${a.date}T${a.time}`);
      const dateTimeB = new Date(`${b.date}T${b.time}`);
      return dateTimeB.getTime() - dateTimeA.getTime();
    });
  };

  // Генерация тестовых данных для всех активов с множественными событиями
  const generateTestDataForAllAssets = () => {
    const stockAssets = ['AAPL', 'GOOGL', 'TSLA', 'MSFT', 'AMZN', 'META', 'NVDA', 'NFLX', 'JPM', 'BAC', 'XOM', 'JNJ', 'PG', 'KO', 'DIS'];
    const forexPairs = ['EURUSD', 'GBPUSD', 'USDJPY', 'USDCHF', 'AUDUSD', 'USDCAD', 'NZDUSD', 'EURGBP', 'EURJPY', 'GBPJPY'];
    const cryptoAssets = ['BTCUSD', 'ETHUSD', 'ADAUSD', 'DOTUSD', 'LTCUSD', 'LINKUSD'];
    const commodities = ['XAUUSD', 'XAGUSD', 'USOIL', 'UKOIL', 'NATGAS', 'COPPER', 'WHEAT', 'CORN']; // ДОБАВЛЕНЫ ТОВАРЫ
    
    const combinedTestData = [];
    const today = new Date();
    
    // Создаем события на следующие 14 дней
    for (let dayOffset = -7; dayOffset <= 7; dayOffset++) {
      const eventDate = new Date(today);
      eventDate.setDate(today.getDate() + dayOffset);
      const dateStr = eventDate.toISOString().split('T')[0];
      
      // Добавляем экономические события для каждого дня
      const dailyEvents = [
        {
          id: `usd-events-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '15:30',
          title: dayOffset === 0 ? 'US Non-Farm Payrolls' : dayOffset === 1 ? 'US CPI Data' : dayOffset === -1 ? 'Fed Interest Rate' : 'US Retail Sales',
          description: 'Key US economic indicator',
          impact: 'high',
          asset: 'USD',
          forecast: dayOffset === 0 ? '200K' : dayOffset === 1 ? '3.2%' : '5.25%',
          actual: dayOffset <= 0 ? (dayOffset === 0 ? '215K' : dayOffset === -1 ? '5.25%' : '3.4%') : null,
          previous: dayOffset === 0 ? '190K' : dayOffset === 1 ? '3.1%' : '5.00%',
          currency: 'USD'
        }
      ];

      if (dayOffset % 2 === 0) {
        dailyEvents.push({
          id: `eur-events-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '11:00',
          title: dayOffset === 0 ? 'ECB Rate Decision' : dayOffset === 2 ? 'Eurozone GDP' : 'EUR PMI Data',
          description: 'European economic indicator',
          impact: 'high',
          asset: 'EUR',
          forecast: dayOffset === 0 ? '4.50%' : dayOffset === 2 ? '0.3%' : '52.1',
          actual: dayOffset <= 0 ? (dayOffset === 0 ? '4.50%' : '0.4%') : null,
          previous: dayOffset === 0 ? '4.25%' : dayOffset === 2 ? '0.2%' : '51.8',
          currency: 'EUR'
        });
      }

      if (dayOffset % 3 === 0) {
        dailyEvents.push({
          id: `gbp-events-${dayOffset}`,
          type: 'economic',
          date: dateStr,
          time: '13:00',
          title: dayOffset === 0 ? 'BoE Rate Decision' : dayOffset === 3 ? 'UK GDP' : 'UK Inflation',
          description: 'UK economic indicator',
          impact: 'high',
          asset: 'GBP',
          forecast: dayOffset === 0 ? '5.25%' : dayOffset === 3 ? '0.2%' : '2.1%',
          actual: dayOffset <= 0 ? (dayOffset === 0 ? '5.25%' : '0.3%') : null,
          previous: dayOffset === 0 ? '5.00%' : dayOffset === 3 ? '0.1%' : '2.0%',
          currency: 'GBP'
        });
      }

      combinedTestData.push(...dailyEvents);
    }

    // Добавляем earnings для акций (распределяем по дням) + дополнительные события
    stockAssets.forEach((asset, index) => {
      // Earnings Report
      const earningsDate = new Date(today);
      earningsDate.setDate(today.getDate() + (index % 10) - 3); // Распределяем на 10 дней
      
      combinedTestData.push({
        id: `earnings-${asset}`,
        type: 'earnings',
        date: earningsDate.toISOString().split('T')[0],
        time: '16:00',
        title: `${asset} Earnings Report`,
        description: 'Quarterly earnings announcement',
        impact: 'high',
        asset: asset,
        forecast: `$${(2 + Math.random()).toFixed(2)}`,
        actual: earningsDate <= today ? `$${(2 + Math.random()).toFixed(2)}` : null,
        previous: `$${(1.8 + Math.random()).toFixed(2)}`,
        currency: 'USD'
      });

      // Dividend Payment (для каждой второй акции)
      if (index % 2 === 0) {
        const dividendDate = new Date(today);
        dividendDate.setDate(today.getDate() + (index % 5) - 2);
        
        combinedTestData.push({
          id: `dividend-${asset}`,
          type: 'dividends',
          date: dividendDate.toISOString().split('T')[0],
          time: '09:30',
          title: `${asset} Dividend Payment`,
          description: 'Quarterly dividend payment',
          impact: 'medium',
          asset: asset,
          amount: `${(0.5 + Math.random()).toFixed(2)}`,
          currency: 'USD'
        });
      }

      // Stock Split (для каждой четвертой акции)
      if (index % 4 === 0) {
        const splitDate = new Date(today);
        splitDate.setDate(today.getDate() + (index % 6) - 1);
        
        combinedTestData.push({
          id: `split-${asset}`,
          type: 'splits',
          date: splitDate.toISOString().split('T')[0],
          time: '09:30',
          title: `${asset} Stock Split`,
          description: 'Stock split announcement',
          impact: 'high',
          asset: asset,
          ratio: index % 2 === 0 ? '2:1' : '3:1',
          currency: 'USD'
        });
      }
    });

    // Добавляем события для криптовалют
    cryptoAssets.forEach((asset, index) => {
      for (let i = 0; i < 3; i++) {
        const cryptoDate = new Date(today);
        cryptoDate.setDate(today.getDate() + (index * 2 + i) - 5);
        
        combinedTestData.push({
          id: `crypto-${asset}-${i}`,
          type: 'economic',
          date: cryptoDate.toISOString().split('T')[0],
          time: `${8 + i * 4}:00`,
          title: `${asset} ${i === 0 ? 'Trading Volume' : i === 1 ? 'Network Update' : 'Market Analysis'}`,
          description: 'Cryptocurrency market event',
          impact: i === 1 ? 'high' : 'medium',
          asset: asset,
          forecast: i === 0 ? 'High' : i === 1 ? 'Bullish' : 'Stable',
          actual: cryptoDate <= today ? (i === 0 ? 'Very High' : i === 1 ? 'Neutral' : 'Volatile') : null,
          previous: i === 0 ? 'Medium' : i === 1 ? 'Bearish' : 'Stable',
          currency: 'USD'
        });
      }
    });

    // ДОБАВЛЯЕМ СОБЫТИЯ ДЛЯ ТОВАРОВ (COMMODITIES)
    commodities.forEach((asset, index) => {
      const commodityNames = {
        'XAUUSD': 'Gold',
        'XAGUSD': 'Silver', 
        'USOIL': 'Crude Oil',
        'UKOIL': 'Brent Oil',
        'NATGAS': 'Natural Gas',
        'COPPER': 'Copper',
        'WHEAT': 'Wheat',
        'CORN': 'Corn'
      };
      
      const commodityName = commodityNames[asset] || asset;
      
      for (let i = 0; i < 2; i++) {
        const commodityDate = new Date(today);
        commodityDate.setDate(today.getDate() + (index % 8) - 4 + i); // Распределяем на 8 дней
        
        const eventTypes = [
          {
            title: `${commodityName} Inventory Report`,
            description: `Weekly ${commodityName.toLowerCase()} inventory data`,
            time: '17:30',
            forecast: asset.includes('OIL') ? '-2.1M barrels' : asset.includes('GOLD') ? '$2050/oz' : 'Bullish',
            actual: commodityDate <= today ? (asset.includes('OIL') ? '-1.8M barrels' : asset.includes('GOLD') ? '$2048/oz' : 'Neutral') : null,
            previous: asset.includes('OIL') ? '-1.5M barrels' : asset.includes('GOLD') ? '$2045/oz' : 'Bearish'
          },
          {
            title: `${commodityName} Price Analysis`,
            description: `Technical analysis and market outlook for ${commodityName.toLowerCase()}`,
            time: '14:00',
            forecast: 'Bullish',
            actual: commodityDate <= today ? 'Neutral' : null,
            previous: 'Bearish'
          }
        ];
        
        const eventType = eventTypes[i % eventTypes.length];
        
        combinedTestData.push({
          id: `commodity-${asset}-${i}`,
          type: 'economic',
          date: commodityDate.toISOString().split('T')[0],
          time: eventType.time,
          title: eventType.title,
          description: eventType.description,
          impact: asset.includes('OIL') || asset.includes('GOLD') ? 'high' : 'medium',
          asset: asset,
          forecast: eventType.forecast,
          actual: eventType.actual,
          previous: eventType.previous,
          currency: 'USD'
        });
      }
    });

    // Добавляем данные для форекса (ИСПРАВЛЕНО: больше событий для валютных пар)
    forexPairs.forEach((asset, index) => {
      const baseCurrency = asset.substring(0, 3);
      const quoteCurrency = asset.substring(3, 6);
      
      // Добавляем несколько событий для каждой пары
      for (let i = 0; i < 3; i++) {
        const eventDate = new Date(today);
        eventDate.setDate(today.getDate() + (index % 7) - 3 + i); // Распределяем события
        
        const eventTypes = [
          { 
            title: `${baseCurrency} Interest Rate Decision`, 
            description: `Central bank monetary policy for ${asset}`,
            time: '15:00',
            forecast: '4.50%',
            actual: eventDate <= today ? '4.50%' : null,
            previous: '4.25%'
          },
          { 
            title: `${baseCurrency} Inflation Data`, 
            description: `Consumer price index for ${baseCurrency}`,
            time: '10:30',
            forecast: '2.1%',
            actual: eventDate <= today ? '2.3%' : null,
            previous: '1.9%'
          },
          { 
            title: `${asset} Technical Analysis`, 
            description: `Market outlook and technical levels`,
            time: '09:00',
            forecast: 'Bullish',
            actual: eventDate <= today ? 'Neutral' : null,
            previous: 'Bearish'
          }
        ];
        
        const eventType = eventTypes[i % eventTypes.length];
        
        combinedTestData.push({
          id: `forex-${asset}-${i}`,
          type: 'economic',
          date: eventDate.toISOString().split('T')[0],
          time: eventType.time,
          title: eventType.title,
          description: eventType.description,
          impact: i === 0 ? 'high' : 'medium',
          asset: asset,
          forecast: eventType.forecast,
          actual: eventType.actual,
          previous: eventType.previous,
          currency: baseCurrency
        });
      }
    });
    
    // ПРАВИЛЬНАЯ СОРТИРОВКА: сегодня сверху, затем будущие события по порядку
    const sortedData = combinedTestData.sort((a, b) => {
      const today = new Date().toISOString().split('T')[0];
      const dateTimeA = new Date(`${a.date}T${a.time}`);
      const dateTimeB = new Date(`${b.date}T${b.time}`);
      
      // Если одно событие сегодня, а другое нет - сегодняшнее идет первым
      if (a.date === today && b.date !== today) return -1;
      if (b.date === today && a.date !== today) return 1;
      
      // Если оба события сегодня - сортируем по времени (раньше сверху)
      if (a.date === today && b.date === today) {
        return dateTimeA.getTime() - dateTimeB.getTime();
      }
      
      // Для будущих событий - хронологический порядок (завтра, послезавтра и т.д.)
      return dateTimeA.getTime() - dateTimeB.getTime();
    });
    
    console.log(`Generated ${sortedData.length} events, sorted by date/time (newest first)`);
    setCalendarData(sortedData);
  };

  // Фильтрация событий
  const filteredEvents = calendarData.filter(event => {
    if (filter === 'all') return true;
    return event.type === filter;
  });

  // Получение цвета по типу события
  const getEventTypeColor = (type) => {
    const colors = {
      earnings: 'bg-blue-500',
      dividends: 'bg-green-500',
      splits: 'bg-purple-500',
      economic: 'bg-orange-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  // Получение иконки по типу события
  const getEventIcon = (type) => {
    switch (type) {
      case 'earnings':
        return <TrendingUp size={16} />;
      case 'dividends':
        return <DollarSign size={16} />;
      case 'splits':
        return <Building2 size={16} />;
      case 'economic':
        return <AlertCircle size={16} />;
      default:
        return <Calendar size={16} />;
    }
  };

  // Получение уровня важности
  const getImpactBadge = (impact) => {
    const styles = {
      high: 'bg-red-100 text-red-800 border-red-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full border ${styles[impact] || styles.low}`}>
        {impact?.toUpperCase() || 'LOW'}
      </span>
    );
  };

  useEffect(() => {
    fetchEconomicData();
    
    // Автоматическое обновление каждые 5 минут
    const intervalId = setInterval(() => {
      console.log('Auto-refreshing economic calendar...');
      fetchEconomicData();
    }, 5 * 60 * 1000); // 5 минут
    
    // Очистка интервала при размонтировании
    return () => clearInterval(intervalId);
  }, []); // Убираем зависимость от selectedAsset

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold text-white">{tt('Экономический календарь')}</h3>
          {loading && (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
              <span className="text-gray-400 text-xs">{tt('Загрузка событий...')}</span>
            </div>
          )}
          {lastUpdated && !loading && (
            <div className="text-xs text-gray-400">
              Обновлено: {lastUpdated.toLocaleTimeString('ru-RU')}
            </div>
          )}
        </div>

        {/* Filters and Controls */}
        <div className="flex items-center space-x-1 lg:space-x-3 flex-wrap lg:flex-nowrap">
          {/* Manual Refresh Button */}
          <button
            onClick={handleManualRefresh}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-xs px-2 lg:px-3 py-1.5 rounded transition-colors flex items-center space-x-1"
            title={tt('Обновить данные')}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{tt('Обновить')}</span>
          </button>
          
          {/* Event Type Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-700 text-white text-sm rounded px-3 py-2 border border-gray-600"
          >
            <option value="all">Все события ({filteredEvents.length})</option>
            <option value="earnings">{tt('Отчеты о прибыли')}</option>
            <option value="dividends">{tt('Дивиденды')}</option>
            <option value="splits">{tt('Сплиты')}</option>
            <option value="economic">{tt('Экономические данные')}</option>
          </select>
        </div>
      </div>

      {/* Events List */}
      {!loading && (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredEvents.length > 0 ? (
            filteredEvents.slice(0, 10).map((event) => (
              <div key={event.id} className="bg-gray-700 rounded-lg p-3 border border-gray-600">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className={`${getEventTypeColor(event.type)} p-2 rounded-full text-white flex-shrink-0`}>
                      {getEventIcon(event.type)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-medium text-sm">{event.title}</h4>
                        {getImpactBadge(event.impact)}
                      </div>
                      
                      <p className="text-gray-300 text-sm mb-2">{event.description}</p>
                      
                      <div className="flex items-center text-xs text-gray-400 space-x-4">
                        <span>📅 {event.date}</span>
                        <span>⏰ {event.time}</span>
                        {event.asset && <span>📈 {event.asset}</span>}
                        {event.currency && <span>💱 {event.currency}</span>}
                      </div>
                      
                      {/* Экономические показатели */}
                      {(event.forecast || event.actual || event.previous || event.type === 'earnings' || event.type === 'economic') && (
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          {(event.forecast && event.forecast !== 'N/A') && (
                            <div className="bg-gray-600 px-2 py-1 rounded">
                              <div className="text-gray-400">{tt('Прогноз')}</div>
                              <div className="text-white font-medium">{event.forecast}</div>
                            </div>
                          )}
                          {(event.actual && event.actual !== 'N/A') && (
                            <div className="bg-gray-600 px-2 py-1 rounded">
                              <div className="text-gray-400">{tt('Факт')}</div>
                              <div className="text-white font-medium">{event.actual}</div>
                            </div>
                          )}
                          {(event.previous && event.previous !== 'N/A') && (
                            <div className="bg-gray-600 px-2 py-1 rounded">
                              <div className="text-gray-400">{tt('Предыдущий')}</div>
                              <div className="text-white font-medium">{event.previous}</div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Дивиденды и сплиты */}
                      {event.amount && (
                        <div className="mt-2 text-sm">
                          <span className="text-green-400 font-medium">${event.amount} per share</span>
                        </div>
                      )}
                      
                      {event.ratio && (
                        <div className="mt-2 text-sm">
                          <span className="text-purple-400 font-medium">Ratio: {event.ratio}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Calendar size={48} className="mx-auto mb-4 opacity-50" />
              <p>{tt('Нет событий для отображения')}</p>
              <p className="text-sm mt-1">{tt('Попробуйте изменить фильтр или актив')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EconomicCalendar;